<template>
  <div v-if="documentUrl && fieldValues">
    <ui-heading tag="h5">
      {{ $t("additional_documents.proofOfAddress.title") }}
    </ui-heading>
    <template v-if="!isPOAEditEnabled">
      <div class="field-list">
        <div
          class="field-item"
          v-for="fieldName in PROOF_OF_ADDRESS_FIELDS"
          :key="fieldName"
        >
          <ui-field
            :label="fieldLabels[fieldName]"
            :variant="fieldValidations[fieldName]"
            label-position="inline"
            bottom-margin="small"
          >
            {{ getFieldValue(fieldName, fieldValues) }}
          </ui-field>
        </div>
      </div>
      <ui-button-group>
        <ui-button
          type="button"
          @click="onEditClick"
          variant="primary"
          v-show="shouldEditBeVisible"
        >
          {{ $t("document_form_buttons.edit") }}
        </ui-button>
        <ui-button
          type="button"
          @click="previewDocument()"
          :disabled="isUpdating"
          variant="primary"
        >
          {{ $t("additional_documents.viewSourceButton") }}
        </ui-button>
      </ui-button-group>
    </template>
    <form v-else @submit.prevent="saveDocument">
      <div class="field-list">
        <div
          class="field-item"
          v-for="fieldName in PROOF_OF_ADDRESS_FIELDS"
          :key="fieldName"
        >
          <ui-field-select
            v-if="fieldName === PROOF_OF_ADDRESS_FIELDS.COUNTRY"
            v-model="fieldValues[fieldName]"
            :label="fieldLabels[fieldName]"
            :error="fieldErrors[fieldName]"
            :variant="fieldValidations[fieldName]"
            :disabled="isUpdating"
            :id="`field-${fieldName}`"
            type="text"
            :filterable="true"
            :options="countryOptions"
            label-position="inline"
            bottom-margin="small"
          />
          <ui-field
            v-else-if="fieldName === PROOF_OF_ADDRESS_FIELDS.ISSUE_DATE"
            label-position="inline"
            :label="fieldLabels[fieldName]"
            :error="fieldErrors[fieldName]"
            :variant="fieldValidations[fieldName]"
            :id="`field-${fieldName}`"
            bottom-margin="small"
          >
            <date-picker
              :input-attr="{ id: `field-${fieldName}` }"
              v-model="fieldValues[fieldName]"
              :disabled="isUpdating"
            />
          </ui-field>
          <ui-field-input
            v-else
            v-model="fieldValues[fieldName]"
            :label="fieldLabels[fieldName]"
            :error="fieldErrors[fieldName]"
            :variant="fieldValidations[fieldName]"
            :disabled="isUpdating"
            :id="`field-${fieldName}`"
            type="text"
            label-position="inline"
            bottom-margin="small"
          />
        </div>
      </div>
      <ui-button-group>
        <ui-button type="submit" variant="primary" :disabled="isUpdating">
          {{ $t("document_form_buttons.submit") }}
        </ui-button>
        <ui-button type="button" :disabled="isUpdating" @click="onEditCancel">
          {{ $t("document_form_buttons.cancel") }}
        </ui-button>
      </ui-button-group>
    </form>
  </div>
</template>
<script>
import { mapActions, mapGetters, mapState } from "vuex";
import DatePicker from "vue2-datepicker";
import { actions, getters } from "@/store";
import { COUNTRY_CODES, NOT_SPECIFIED } from "@/constants";
import { getMostRecentDocument } from "@/utils/documentPhoto";
import { getPOAValidation } from "@/utils/form";
import { localISODateString } from "@/utils/date";
import { getFieldError } from "../../utils/form";
import { PROOF_OF_ADDRESS_FIELDS } from "../../constants/proofOfAddress";
import { MEDIA_ADDITIONAL_DOCUMENT_TYPE } from "../../constants";
import { handleDocumentPreview } from "./utils";
import { notificationHandlers } from "./Notification.mixins";

export default {
  components: { DatePicker },
  props: {
    complianceReviewIdentificationId: { type: String, default: null },
    isUpdating: { type: Boolean, default: false }
  },
  data() {
    const initializedFields = Object.values(PROOF_OF_ADDRESS_FIELDS).reduce(
      (accumulator, fieldKey) => {
        accumulator[fieldKey] = "";
        return accumulator;
      },
      {}
    );

    return {
      PROOF_OF_ADDRESS_FIELDS,
      originalFieldValues: null,
      fieldValues: initializedFields,
      fieldValidations: null,
      documentBlob: null
    };
  },
  mixins: [notificationHandlers],
  computed: {
    ...mapState({
      work: state => state.work
    }),
    ...mapGetters([
      getters.currentIdentification,
      getters.isPOAEditEnabled,
      getters.isComplianceReviewItemResolved
    ]),
    shouldEditBeVisible() {
      if (
        !!this.complianceReviewIdentificationId &&
        this.isComplianceReviewItemResolved
      ) {
        return false;
      }

      return true;
    },
    countryOptions() {
      return [
        {
          label: `${this.$t("not_specified")} (${NOT_SPECIFIED})`,
          value: NOT_SPECIFIED
        },
        ...Object.values(COUNTRY_CODES)
          .map(country => ({
            value: country,
            label: `${this.$t(`countries.${country}`)} (${country})`
          }))
          .sort(({ label: a }, { label: b }) =>
            a.localeCompare(b, this.$i18n.locale)
          )
      ];
    },
    document() {
      return this.currentIdentification?.data?.media?.filter(
        item =>
          item.givenDocumentType ===
          MEDIA_ADDITIONAL_DOCUMENT_TYPE.PROOF_OF_ADDRESS
      );
    },

    documentUrl() {
      return getMostRecentDocument(this.document)?.url;
    },

    documentExtensionType() {
      return getMostRecentDocument(this.document)?.fileExtension;
    },

    fieldLabels() {
      return Object.values(PROOF_OF_ADDRESS_FIELDS).reduce(
        (target, fieldName) => {
          target[fieldName] =
            this.$t(`additional_documents.proofOfAddress.${fieldName}`) || null;
          return target;
        },
        {}
      );
    },

    fieldErrors() {
      if (!this.fieldValues) {
        return;
      }

      const validation = getPOAValidation();

      return Object.entries(this.fieldValues).reduce((target, [key, value]) => {
        const fieldValidation = validation[key];
        const errorMessageKey = getFieldError({
          ...fieldValidation,
          value
        });

        target[key] =
          (errorMessageKey &&
            this.$t(`input_error.${errorMessageKey}`, fieldValidation)) ||
          null;
        return target;
      }, {});
    },

    firstError() {
      if (!this.fieldErrors) {
        return null;
      }

      for (const key in this.fieldErrors) {
        const errorValue = this.fieldErrors[key];

        if (this.fieldErrors[key]) {
          return {
            key,
            value: errorValue
          };
        }
      }

      return null;
    }
  },

  methods: {
    ...mapActions([actions.putProofOfAddress, actions.notificationAdd]),

    async previewDocument() {
      this.documentBlob = await handleDocumentPreview(
        this.documentUrl,
        this.documentExtensionType,
        this.documentBlob,
        this.displayNotification
      );
    },

    onEditClick() {
      if (this.isPOAEditEnabled) {
        return;
      }

      this.$nextTick(() => {
        document.getElementById(`field-${PROOF_OF_ADDRESS_FIELDS[0]}`)?.focus();
      });

      this.originalFieldValues = { ...this.fieldValues };
      this.$store.commit("changePOAEditStatus", true);
    },

    onEditCancel() {
      this.fieldValues = { ...this.originalFieldValues };
      this.originalFieldValues = null;
      this.$store.commit("changePOAEditStatus", false);
    },

    getNormalizedValue(key, value) {
      if (key === PROOF_OF_ADDRESS_FIELDS.ISSUE_DATE) {
        return localISODateString(value);
      }

      return value;
    },

    cleanValues(values) {
      if (!values) {
        throw new Error("PoaUpdateDateCanNotBeNull");
      }

      return Object.entries(values).reduce((target, [key, value]) => {
        if (value != null) {
          target[key] = this.getNormalizedValue(key, value);
        }

        return target;
      }, {});
    },

    saveDocument() {
      if (this.firstError) {
        const { key, value } = this.firstError;

        const label = this.fieldLabels[key];

        this.$nextTick(() => {
          document.getElementById(`field-${key}`)?.focus();
        });

        this.notificationAdd({
          title: this.$t("checks_registries_reason.DOCUMENT_IS_NOT_VALID"),
          message: `${label}: ${value}`
        });

        return;
      }

      this.putProofOfAddress({
        identificationId: this.work.identificationId,
        poa: this.cleanValues(this.fieldValues)
      });
    },

    setInitialFieldValues(proofOfAddressValue) {
      if (!proofOfAddressValue) {
        return;
      }

      this.fieldValidations = {};

      for (const fieldName of Object.values(PROOF_OF_ADDRESS_FIELDS)) {
        const valueData = proofOfAddressValue?.[fieldName];
        let value = valueData?.value || "";

        if (fieldName === PROOF_OF_ADDRESS_FIELDS.ISSUE_DATE && value) {
          value = new Date(value);
        }

        this.fieldValues[fieldName] = value;

        this.fieldValidations[fieldName] = valueData?.isValid
          ? "success"
          : "error";
      }
    },

    getFieldValue(fieldName, fieldValues) {
      const value = fieldValues[fieldName];

      if (!fieldValues[fieldName]) {
        return "-";
      }

      if (fieldName === PROOF_OF_ADDRESS_FIELDS.COUNTRY) {
        const countryCode = value;
        const countryName = this.$t(`countries.${countryCode}`);

        return `${countryName} (${countryCode})`;
      }

      if (fieldName === PROOF_OF_ADDRESS_FIELDS.ISSUE_DATE) {
        return localISODateString(value);
      }

      return value;
    }
  },

  watch: {
    "currentIdentification.data.proofOfAddress": {
      handler(proofOfAddressValue) {
        this.setInitialFieldValues(proofOfAddressValue);
        this.$store.commit("changePOAEditStatus", false);
      },
      immediate: true
    }
  }
};
</script>
<style scoped lang="scss">
.field-item {
  ::v-deep input,
  ::v-deep textarea,
  ::v-deep select,
  ::v-deep .field-input {
    text-transform: uppercase;
  }

  ::v-deep label {
    max-width: 45%;
  }

  ::v-deep .field-input.label-inline.fixed {
    align-self: center;
  }

  ::v-deep .mx-input {
    color: var(--text-color-base);
    background-color: var(--input-default-background);
    border: var(--input-border);
  }

  ::v-deep i svg {
    fill: var(--text-color-base);
  }
}

.field-input .mx-datepicker {
  width: 100%;
}
</style>
